import { Alert, Button } from "@svea/design-system-components-react";
import {
	AriaPopover,
	ButtonGroup,
	FormHelperText,
	Grid,
	Heading,
	Markdown,
	omit,
	P,
	type SetOptional,
	Stack,
	useText,
} from "@sveabank/cc-common";
import arrayMutators from "final-form-arrays";
import { useMemo, useState } from "react";
import { Field, Form } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { usePageContext } from "../../../../hooks/context";
import type {
	ClcBeneficialOwner,
	ClcBeneficialOwnerFormValue,
	ClcBeneficialOwnership,
	ClcBeneficialOwnershipForm,
	ClcShareholder,
} from "../../../../types/clc-application";
import { useClcCompanyApplication } from "../../hooks";
import { getIsPersonShareholder, getTotalDistributedShares } from "../../utils";
import { AddBeneficialOwnerModal } from "./add-beneficial-owner-modal";
import BeneficialOwnerField from "./beneficial-owner-field";

type Props = {
	onSubmit: (data: ClcBeneficialOwnership) => Promise<void>;
	onCancel: () => void;
};

type BeneficialOwnersValidationResult = {
	hasNoBeneficialOwners?: boolean;
	missingBeneficialOwnerDetails?: boolean;
};

const hasNoBeneficialOwners = (values: ClcBeneficialOwnershipForm) =>
	!values.beneficialOwners?.length;

const hasMissingBeneficialOwnerDetails = (values: ClcBeneficialOwnershipForm) =>
	(values.beneficialOwners ?? []).some(
		(owner) => !owner.ownershipPercent || !owner.controlTypes?.length,
	);

const validateBeneficialOwners = (values: ClcBeneficialOwnershipForm) => {
	if (hasNoBeneficialOwners(values)) {
		return { hasNoBeneficialOwners: true };
	}

	if (hasMissingBeneficialOwnerDetails(values)) {
		return { missingBeneficialOwnerDetails: true };
	}

	return {};
};

const isMajorShareholder = (shareholder: ClcShareholder, totalNumberOfShares: number) =>
	shareholder.numberOfShares / totalNumberOfShares > 0.25;

const ClcBeneficialOwners = ({ onSubmit, onCancel }: Props) => {
	const {
		clcApplicationDetails: {
			beneficialOwners: { value: beneficialOwnership },
			applicant,
			companyShares: { value: shareholdersValue },
		},
		isPostingClcBeneficialOwnership,
	} = usePageContext(useClcCompanyApplication);

	const currentBeneficialOwners = beneficialOwnership?.beneficialOwners || [];
	const currentShareholders = shareholdersValue?.shareholders || [];
	const totalNumberOfShares = getTotalDistributedShares(currentShareholders);

	const beneficialOwnersToFormValues = (
		beneficialOwners: ClcBeneficialOwner[],
	): ClcBeneficialOwnerFormValue[] => {
		return beneficialOwners.map((bo) => ({
			...omit(bo, "ownershipPercentMax", "ownershipPercentMin"),
			ownershipPercent: bo.ownershipPercentMin
				?.toString()
				.concat("-", bo.ownershipPercentMax?.toString() || ""),
		}));
	};

	const shareholdersAsBeneficialOwners = useMemo(
		(): ClcBeneficialOwnerFormValue[] =>
			currentShareholders
				.filter((shareholder) => isMajorShareholder(shareholder, totalNumberOfShares))
				.map((shareholder) => ({
					controlTypes: ["SharesOwnershipAgreement"],
					firstName: shareholder.firstName,
					lastName: shareholder.lastName,
					nationalId: shareholder.nationalId,
					postalAddress: shareholder.postalAddress,
					postCode: shareholder.postCode,
					town: shareholder.town,
					ownershipPercent: undefined,
				})),
		[shareholdersValue],
	);

	const initialValues: ClcBeneficialOwnershipForm = useMemo(
		() => ({
			beneficialOwners: [
				...beneficialOwnersToFormValues(currentBeneficialOwners),
				...shareholdersAsBeneficialOwners.filter(
					({ nationalId }) => !currentBeneficialOwners.find((b) => b.nationalId === nationalId),
				),
			],
		}),
		[beneficialOwnership, shareholdersAsBeneficialOwners],
	);

	const [t, texts] = useText();
	const [isAddBoModalOpen, setIsAddBoModalOpen] = useState(false);

	const submitForm = (values: ClcBeneficialOwnershipForm) => {
		const selectedValues: ClcBeneficialOwnership = {
			beneficialOwners: values.beneficialOwners
				// Transform form values back to the format expected by BE
				.map((bo) => {
					if (!bo.ownershipPercent) throw new Error("Ownership percent not set");

					const { ownershipPercent: _, ...rest } = {
						...bo,
						ownershipPercentMin: +(bo.ownershipPercent.split("-")?.[0] ?? 0),
						ownershipPercentMax: +(bo.ownershipPercent.split("-")?.[1] ?? 0),
					};
					return rest;
				}),
		};

		onSubmit(selectedValues);
	};

	return (
		<Grid>
			<Grid.Col
				span={{
					base: 12,
					tablet: 8,
				}}
			>
				<Stack gap={4}>
					<Stack direction="row" gap={1}>
						<Heading.H3 size="h4" italic={false} className="text-text-primary">
							{t(texts.newCompany.beneficialOwnership.title)}
						</Heading.H3>

						<AriaPopover
							variant="info"
							content={() => (
								<Markdown>{t(texts.newCompany.beneficialOwnership.helpText1)}</Markdown>
							)}
						/>
					</Stack>

					<P>{t(texts.newCompany.beneficialOwnership.descriptionExtra)}</P>

					<Form<ClcBeneficialOwnershipForm>
						mutators={{
							...arrayMutators,
						}}
						validate={validateBeneficialOwners}
						onSubmit={submitForm}
						initialValues={initialValues}
						render={({
							handleSubmit,
							submitFailed,
							hasValidationErrors,
							errors,
							values,
							form: {
								mutators: { push },
							},
						}) => {
							const beneficialOwners = values.beneficialOwners ?? [];
							const validationErrors = errors as BeneficialOwnersValidationResult | undefined;
							const allAddedNationalIds = beneficialOwners.map((b) => b.nationalId);

							return (
								<form onSubmit={handleSubmit} name="companyRegistrationForm">
									<Stack gap={8}>
										{beneficialOwners.length === 0 && (
											<Alert
												kind={submitFailed ? "error" : "warning"}
												title={t(
													texts.newCompany.beneficialOwnership.noBeneficialOwnersWarningTitle,
												)}
											>
												<P className="mb-2">
													{t(texts.newCompany.beneficialOwnership.noBeneficialOwnersWarningBody1)}
												</P>
												<P>
													{t(texts.newCompany.beneficialOwnership.noBeneficialOwnersWarningBody2)}
												</P>
											</Alert>
										)}

										<FieldArray<ClcBeneficialOwner> name="beneficialOwners">
											{({ fields }) => {
												return (
													<Stack gap={2} align="start">
														{fields.map((name, index) => (
															<Field
																name={name}
																key={name}
																render={() => {
																	const person = fields.value[index];
																	if (!person) return null; // should never occur

																	const isApplicant = person.nationalId === applicant.nationalId;

																	const isShareholder = getIsPersonShareholder(
																		person.nationalId,
																		shareholdersValue?.shareholders,
																	);

																	const deleteBo = () => fields.remove(index);

																	return (
																		<BeneficialOwnerField
																			isApplicant={isApplicant}
																			isShareholder={isShareholder}
																			isMajorShareholder={shareholdersAsBeneficialOwners.some(
																				(bo) => bo.nationalId === person.nationalId,
																			)}
																			name={name}
																			onDelete={deleteBo}
																			person={person}
																		/>
																	);
																}}
															/>
														))}

														<Button
															variant="plain"
															iconStart="plusCircle"
															onPress={() => setIsAddBoModalOpen(true)}
														>
															{t(texts.newCompany.beneficialOwnership.addBeneficialOwner)}
														</Button>
													</Stack>
												);
											}}
										</FieldArray>
										<Stack gap={2}>
											<ButtonGroup row align="start">
												<Button type="button" variant="secondary" onPress={onCancel}>
													{t(texts.common.abort)}
												</Button>
												<Button
													type="submit"
													isPending={isPostingClcBeneficialOwnership}
													data-tracking-action="CLC Application Continue"
													data-tracking-name="Beneficial Owners"
												>
													{t(texts.common.continue)}
												</Button>
											</ButtonGroup>
											{submitFailed && hasValidationErrors && (
												<FormHelperText error>
													{t(
														validationErrors?.missingBeneficialOwnerDetails
															? texts.validation.missingBeneficialOwnerDetails
															: texts.validation.hasNoBeneficialOwners,
													)}
												</FormHelperText>
											)}
										</Stack>
									</Stack>

									<AddBeneficialOwnerModal
										open={isAddBoModalOpen}
										setOpen={setIsAddBoModalOpen}
										addBeneficialOwner={(
											newBeneficialOwners: Array<
												SetOptional<
													ClcBeneficialOwner,
													"ownershipPercentMin" | "ownershipPercentMax"
												>
											>,
										) => {
											push?.("beneficialOwners", ...newBeneficialOwners);
										}}
										addedNationalIds={allAddedNationalIds.filter(
											(id) => id !== applicant.nationalId,
										)}
									/>
								</form>
							);
						}}
					/>
				</Stack>
			</Grid.Col>
		</Grid>
	);
};

export default ClcBeneficialOwners;
